import * as React from 'react';
import type { Participant } from 'livekit-client';
import { Track } from 'livekit-client';
import type { ParticipantClickEvent, TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { isTrackReference } from '@livekit/components-core';
import {
  ParticipantContext,
  TrackRefContext,
  useEnsureTrackRef,
  useFeatureContext,
  useMaybeParticipantContext,
  useMaybeTrackRefContext,
  useParticipantTile,
  ConnectionQualityIndicator,
  ParticipantName,
  TrackMutedIndicator,
  VideoTrack,
  AudioTrack,
  FocusToggle,
} from '@livekit/components-react';
import { VoiceAssistantTile } from './VoiceAssistantTile';

export function ParticipantContextIfNeeded(
  props: React.PropsWithChildren<{
    participant?: Participant;
  }>
) {
  const hasContext = !!useMaybeParticipantContext();
  return props.participant && !hasContext ? (
    <ParticipantContext.Provider value={props.participant}>
      {props.children}
    </ParticipantContext.Provider>
  ) : (
    <>{props.children}</>
  );
}

export function TrackRefContextIfNeeded(
  props: React.PropsWithChildren<{
    trackRef?: TrackReferenceOrPlaceholder;
  }>
) {
  const hasContext = !!useMaybeTrackRefContext();
  return props.trackRef && !hasContext ? (
    <TrackRefContext.Provider value={props.trackRef}>{props.children}</TrackRefContext.Provider>
  ) : (
    <>{props.children}</>
  );
}

export interface ParticipantTileProps extends React.HTMLAttributes<HTMLDivElement> {
  trackRef?: TrackReferenceOrPlaceholder;
  disableSpeakingIndicator?: boolean;
  isHumanVideoOff?: boolean;

  onParticipantClick?: (event: ParticipantClickEvent) => void;
}

export const ParticipantTile: (
  props: ParticipantTileProps & React.RefAttributes<HTMLDivElement>
) => React.ReactNode = React.forwardRef<HTMLDivElement, ParticipantTileProps>(
  function ParticipantTile(
    {
      trackRef,
      children,
      onParticipantClick,
      disableSpeakingIndicator,
      isHumanVideoOff,
      ...htmlProps
    }: ParticipantTileProps,
    ref
  ) {
    const trackReference = useEnsureTrackRef(trackRef);

    const { elementProps } = useParticipantTile<HTMLDivElement>({
      htmlProps,
      disableSpeakingIndicator,
      onParticipantClick,
      trackRef: trackReference,
    });

    const autoManageSubscription = useFeatureContext()?.autoSubscription;

    const isVideoEnabled = React.useMemo(() => {
      if (!isTrackReference(trackReference)) return false;

      if (trackReference.source === Track.Source.Camera) {
        return (
          trackReference.publication?.isSubscribed &&
          !trackReference.publication?.isMuted &&
          trackReference.publication?.track !== undefined
        );
      }
      return false;
    }, [trackReference]);

    const isAgent = trackReference?.participant?.identity?.startsWith('agent-');

    if (!isAgent && !isVideoEnabled) {
      return null;
    }

    const isFullScreenAgent = isAgent && isHumanVideoOff;

    return (
      <div
        ref={ref}
        style={{
          position: 'relative',
          backgroundColor: 'white',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          ...(isFullScreenAgent && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            maxWidth: '100vw',
            maxHeight: '100vh',
          }),
          ...elementProps.style,
        }}
        className={`${elementProps.className || ''} ${isFullScreenAgent ? 'fullscreen-agent' : ''}`}
        {...elementProps}
      >
        <TrackRefContextIfNeeded trackRef={trackReference}>
          <ParticipantContextIfNeeded participant={trackReference.participant}>
            {children ?? (
              <>
                {isVideoEnabled ? (
                  isTrackReference(trackReference) &&
                  (trackReference.publication?.kind === 'video' ||
                    trackReference.source === Track.Source.Camera ||
                    trackReference.source === Track.Source.ScreenShare) ? (
                    <VideoTrack
                      trackRef={trackReference}
                      manageSubscription={autoManageSubscription}
                    />
                  ) : null
                ) : (
                  isAgent && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'black',
                        width: '100%',
                        height: '100%',
                        maxWidth: '100vw',
                        maxHeight: '100vh',
                        overflow: 'hidden',
                        zIndex: 1,
                      }}
                    >
                      <VoiceAssistantTile />
                      <div className="lk-participant-metadata" style={{ marginTop: 16 }}>
                        <div className="lk-participant-metadata-item">
                          {trackReference.source === Track.Source.Camera ? (
                            <>
                              <TrackMutedIndicator
                                trackRef={{
                                  participant: trackReference.participant,
                                  source: Track.Source.Microphone,
                                }}
                                show={'muted'}
                              ></TrackMutedIndicator>
                              <ParticipantName />
                            </>
                          ) : (
                            <>
                              <ParticipantName>&apos;s screen</ParticipantName>
                            </>
                          )}
                        </div>
                        <ConnectionQualityIndicator className="lk-participant-metadata-item" />
                      </div>
                    </div>
                  )
                )}
                {isTrackReference(trackReference) &&
                  trackReference.publication?.kind === 'audio' && (
                    <AudioTrack trackRef={trackReference} />
                  )}
              </>
            )}
            <FocusToggle trackRef={trackReference} />
          </ParticipantContextIfNeeded>
        </TrackRefContextIfNeeded>
      </div>
    );
  }
);
